/**
 * Unsent form drafts, so leaving a screen mid-sentence does not throw the words
 * away.
 *
 * `sessionStorage`, not `localStorage` and not `redux-persist`. A draft is a
 * half-finished thought, useful for as long as the tab is open and worth nothing
 * after that; `localStorage` would keep a frank remark about a named candidate on
 * the machine indefinitely. It is also per tab, which is the behaviour you want:
 * two tabs on two candidates keep two drafts.
 *
 * Every key is namespaced under `PREFIX`, so `clearAllDrafts` can drop the lot
 * without knowing who wrote what. `authService.clearAuthStorage` calls it on
 * logout, for the same reason it clears the SuperAdmin's tenant scope: the next
 * account to sign in on this browser must not find the last one's notes.
 *
 * Nothing here throws. Storage can be full, disabled, or absent under a test
 * renderer, and a draft is a convenience — losing one is a worse outcome than
 * the old behaviour, but taking the screen down with it is worse still.
 */

const PREFIX = "draft:";

const store = () => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    // Storage access itself throws when cookies are blocked for the origin.
    return null;
  }
};

/**
 * Read one draft.
 *
 * @param {string} key  a caller-chosen key, namespaced internally
 * @returns {any|null}  whatever was saved, or null
 */
export const readDraft = (key) => {
  if (!key) return null;
  try {
    const raw = store()?.getItem(PREFIX + key);
    return raw == null ? null : JSON.parse(raw);
  } catch {
    // A key written by an older shape of the form. Treat it as absent.
    return null;
  }
};

/**
 * Save one draft, or clear it when the caller passes `null`.
 *
 * Emptiness is the caller's call, not this module's. A note draft holds a body
 * and the scope it was typed under, so "every value is empty" is the wrong test:
 * an emptied body beside a live scope would read as still worth keeping, and the
 * next mount would restore a blank draft over a form that looks untouched. The
 * screen knows which part is the content. It passes `null` when there is none.
 *
 * @param {string} key
 * @param {any} value  `null` or `""` clears
 */
export const writeDraft = (key, value) => {
  if (!key) return;
  if (value == null || value === "") return clearDraft(key);
  try {
    store()?.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota, or storage disabled. The draft is a convenience.
  }
};

/**
 * Drop one draft. Call it once the thing has actually been sent.
 *
 * @param {string} key
 */
export const clearDraft = (key) => {
  if (!key) return;
  try {
    store()?.removeItem(PREFIX + key);
  } catch {
    /* nothing to do */
  }
};

/**
 * Drop every draft in this tab. Called on logout.
 */
export const clearAllDrafts = () => {
  const s = store();
  if (!s) return;
  try {
    Object.keys(s)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => s.removeItem(key));
  } catch {
    /* nothing to do */
  }
};
